'use client';

import { useTranslations } from 'next-intl';
import { FileText, Users, CreditCard, Calendar, AlertTriangle, Scale, Shield } from 'lucide-react';
import LegalPageLayout from '@/components/legal/LegalPageLayout';

export default function AGB() {
  const t = useTranslations();

  return (
    <LegalPageLayout
      titleKey="legal.terms.title"
      subtitleKey="legal.terms.subtitle"
      icon={FileText}
      iconColor="text-amber-600"
    >
      {/* Scope Section */}
      <div className="mb-8 rounded-2xl bg-gray-50 p-8">
        <div className="mb-4 flex items-center">
          <Scale className="mr-3 h-8 w-8 text-amber-600" />
          <h2 className="text-2xl font-bold text-gray-950">
            {t('legal.terms.sections.scope.title')}
          </h2>
        </div>
        <p className="mb-4 text-amber-700">{t('legal.terms.sections.scope.intro')}</p>
        <ul className="ml-4 space-y-2 text-amber-700">
          {(t.raw('legal.terms.sections.scope.points') as string[]).map((point, index) => (
            <li key={index}>• {point}</li>
          ))}
        </ul>
      </div>

      {/* Services Section */}
      <div className="mb-8 rounded-2xl bg-gray-50 p-8">
        <div className="mb-4 flex items-center">
          <Calendar className="mr-3 h-8 w-8 text-blue-600" />
          <h2 className="text-2xl font-bold text-gray-950">
            {t('legal.terms.sections.services.title')}
          </h2>
        </div>
        <p className="mb-4 text-blue-700">{t('legal.terms.sections.services.intro')}</p>
        <ul className="ml-4 space-y-2 text-blue-700">
          {(t.raw('legal.terms.sections.services.features') as string[]).map((feature, index) => (
            <li key={index}>• {feature}</li>
          ))}
        </ul>
        <p className="mt-4 text-blue-700">{t('legal.terms.sections.services.availability')}</p>
      </div>

      {/* User Accounts Section */}
      <div className="mb-8 rounded-2xl bg-gray-50 p-8">
        <div className="mb-4 flex items-center">
          <Users className="mr-3 h-8 w-8 text-purple-600" />
          <h2 className="text-2xl font-bold text-gray-950">
            {t('legal.terms.sections.accounts.title')}
          </h2>
        </div>
        <p className="mb-4 text-purple-700">{t('legal.terms.sections.accounts.intro')}</p>

        <div className="mb-4 rounded-2xl border border-gray-200/80 bg-white p-4">
          <h3 className="mb-2 font-semibold text-gray-700">
            {t('legal.terms.sections.accounts.registration.title')}
          </h3>
          <p className="text-purple-700">{t('legal.terms.sections.accounts.registration.text')}</p>
        </div>

        <div className="rounded-2xl border border-gray-200/80 bg-white p-4">
          <h3 className="mb-2 font-semibold text-gray-700">
            {t('legal.terms.sections.accounts.responsibility.title')}
          </h3>
          <p className="text-purple-700">{t('legal.terms.sections.accounts.responsibility.text')}</p>
        </div>
      </div>

      {/* Payment Section */}
      <div className="mb-8 rounded-2xl bg-gray-50 p-8">
        <div className="mb-4 flex items-center">
          <CreditCard className="mr-3 h-8 w-8 text-emerald-600" />
          <h2 className="text-2xl font-bold text-gray-950">
            {t('legal.terms.sections.payment.title')}
          </h2>
        </div>

        <div className="mb-4 rounded-2xl border border-gray-200/80 bg-white p-4">
          <h3 className="mb-2 font-semibold text-gray-700">
            {t('legal.terms.sections.payment.subscription.title')}
          </h3>
          <p className="text-emerald-700">{t('legal.terms.sections.payment.subscription.text')}</p>
        </div>

        <div className="mb-4 rounded-2xl border border-gray-200/80 bg-white p-4">
          <h3 className="mb-2 font-semibold text-gray-700">
            {t('legal.terms.sections.payment.billing.title')}
          </h3>
          <p className="text-emerald-700">{t('legal.terms.sections.payment.billing.text')}</p>
        </div>

        <div className="rounded-2xl border border-gray-200/80 bg-white p-4">
          <h3 className="mb-2 font-semibold text-gray-700">
            {t('legal.terms.sections.payment.cancellation.title')}
          </h3>
          <p className="text-emerald-700">{t('legal.terms.sections.payment.cancellation.text')}</p>
        </div>
      </div>

      {/* Third Party Services Section */}
      <div className="mb-8 rounded-2xl bg-gray-50 p-8">
        <div className="mb-4 flex items-center">
          <Shield className="mr-3 h-8 w-8 text-indigo-600" />
          <h2 className="text-2xl font-bold text-gray-950">
            {t('legal.terms.sections.thirdParty.title')}
          </h2>
        </div>
        <p className="mb-4 text-indigo-700">{t('legal.terms.sections.thirdParty.intro')}</p>

        <div className="mb-4 rounded-2xl border border-gray-200/80 bg-white p-4">
          <h3 className="mb-2 font-semibold text-gray-700">
            {t('legal.terms.sections.thirdParty.stripe.title')}
          </h3>
          <p className="text-indigo-700">{t('legal.terms.sections.thirdParty.stripe.text')}</p>
        </div>

        <div className="rounded-2xl border border-gray-200/80 bg-white p-4">
          <h3 className="mb-2 font-semibold text-gray-700">
            {t('legal.terms.sections.thirdParty.calendar.title')}
          </h3>
          <p className="text-indigo-700">{t('legal.terms.sections.thirdParty.calendar.text')}</p>
        </div>
      </div>

      {/* Liability Section */}
      <div className="mb-8 rounded-2xl bg-gray-50 p-8">
        <div className="mb-4 flex items-center">
          <AlertTriangle className="mr-3 h-8 w-8 text-red-600" />
          <h2 className="text-2xl font-bold text-gray-950">
            {t('legal.terms.sections.liability.title')}
          </h2>
        </div>
        <p className="mb-4 text-red-700">{t('legal.terms.sections.liability.intro')}</p>
        <ul className="ml-4 space-y-2 text-red-700">
          {(t.raw('legal.terms.sections.liability.exclusions') as string[]).map((exclusion, index) => (
            <li key={index}>• {exclusion}</li>
          ))}
        </ul>
      </div>

      {/* Final Provisions Section */}
      <div className="mb-8 rounded-2xl bg-gray-50 p-8">
        <h2 className="mb-4 text-2xl font-bold text-gray-900">
          {t('legal.terms.sections.final.title')}
        </h2>
        <p className="mb-4 text-gray-700">{t('legal.terms.sections.final.law')}</p>
        <p className="mb-4 text-gray-700">{t('legal.terms.sections.final.jurisdiction')}</p>
        <p className="text-gray-700">{t('legal.terms.sections.final.severability')}</p>
      </div>

      {/* Footer */}
      <div className="mt-12 rounded-2xl bg-gray-100 p-6 text-center">
        <FileText className="mx-auto mb-3 h-8 w-8 text-gray-600" />
        <p className="text-sm text-gray-600">
          {t('legal.terms.lastUpdated', { date: new Date().toLocaleDateString() })}
        </p>
        <p className="mt-2 text-xs text-gray-500">
          {t('legal.terms.contact')}{' '}
          <a
            href={`mailto:${t('legal.company.email')}`}
            className="text-blue-600 hover:underline"
          >
            {t('legal.company.email')}
          </a>
        </p>
      </div>
    </LegalPageLayout>
  );
}
